import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography, makeStyles } from '@material-ui/core';
import ArrowTooltip from '../arrowTooltip';
import TextBoxWithTooltip from '../textBoxWithTooltip';
import CustomButton from '../customButton';
import { ReactComponent as EditSalariesIcon } from '../../../assets/icons/elevator.svg';
import { isEmpty } from '../../../utility/validation';
import Scrollbars from '../scrollbars';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    padding: spacing(8, 10.5, 8, 8),
  },
  wrapper: {
    gap: 4,
    alignItems: 'center',
    display: 'flex',
    height: 28,
  },
  buttonTextBoldMedium: {
    marginLeft: spacing(2),
    minWidth: 'fit-content',
    border: `1px solid ${primary.bluish1}`,
    borderRadius: 44,
    color: primary.bluish1,
    lineHeight: '14px',
    justifySelf: 'center',
    padding: spacing(1, 3),
    maxHeight: 22,
    fontSize: 12,
    textTransform: 'none',
    fontFamily: 'ProximaNova-Bold',
  },
  tooltipWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: spacing(5.5),
    borderBottom: `1px solid ${primary.bluish7}`,
  },
  salaries: {
    paddingTop: spacing(5.5),
    gap: spacing(6),
    display: 'grid',
  },
  salary: {
    paddingBottom: 0,
    borderBottom: 'none',
  },
  salaryRange: {
    display: 'flex',
    maxWidth: 180,
    justifyContent: 'end',
  },
  level: {
    maxWidth: 150,
  },
  tooltip: {
    maxWidth: 445,
    width: 445,
  },
  salaryViewport: {
    maxHeight: 300,
  },
  scrollY: {
    right: -16,
  },
}));

const SalaryTooltip = ({
  translations,
  title,
  position = 'top',
  track = {},
  currency = '',
  canEdit = false,
  onClick = () => {},
  ...rest
}) => {
  const classes = useStyles();

  const handleClick = e => {
    e.stopPropagation();
    onClick();
  };

  const renderContent = () => {
    return (
      <div className={classes.root}>
        <div className={classes.tooltipWrapper}>
          <TextBoxWithTooltip variant="h5" text={title} />
          {canEdit && (
            <CustomButton
              className={classes.buttonTextBoldMedium}
              type="addWithTextRounded"
              onClick={handleClick}
            >
              {translations.editSalaries}
            </CustomButton>
          )}
        </div>
        <Scrollbars
          viewportClass={classes.salaryViewport}
          verticalScrollClass={classes.scrollY}
        >
          <div className={classes.salaries}>
            {track.levels.map((level, index) => {
              const salaryRange =
                isEmpty(currency) && level.minSalary && level.maxSalary
                  ? `${currency} ${level.minSalary} - ${currency} ${level.maxSalary}`
                  : '-';
              return (
                <div
                  key={`salary_${index}`}
                  className={classNames(classes.tooltipWrapper, classes.salary)}
                >
                  <div className={classes.level}>
                    <TextBoxWithTooltip variant="subtitle1" text={level.name} />
                  </div>
                  <div className={classes.salaryRange}>
                    <TextBoxWithTooltip
                      variant="subtitle1"
                      text={salaryRange}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </Scrollbars>
      </div>
    );
  };

  return (
    <ArrowTooltip
      isArrowHidden
      tooltipLabel={renderContent()}
      tooltipClassName={classes.tooltip}
      position={position}
      {...rest}
    >
      <div className={classes.wrapper}>
        <EditSalariesIcon />
        <Typography variant="subtitle2">{translations.salaryRange}</Typography>
      </div>
    </ArrowTooltip>
  );
};

SalaryTooltip.propTypes = {
  translations: PropTypes.object.isRequired,
  track: PropTypes.object,
  onClick: PropTypes.func,
  currency: PropTypes.string,
  position: PropTypes.string,
  canEdit: PropTypes.bool,
  title: PropTypes.string.isRequired,
};

export default SalaryTooltip;
